import { Injectable, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import {
  bufferCount,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { DataCloudService } from '@fcom/common/datacloud';
import { GtmService } from '@fcom/common/gtm';
import { GtmEvent, GtmFlightSelectionData, GtmPurchaseFlow } from '@fcom/common/interfaces';
import { entrySet, equals, unsubscribe } from '@fcom/core/utils';
import { FinnairCart, ServiceFlow } from '@fcom/dapi/api/models';
import { orderData } from '@fcom/common-booking/store';
import { PaymentStatus } from '@fcom/common-booking/interfaces';
import { paymentStatus } from '@fcom/common-booking/store/selectors';
import { isAwardBooking } from '@fcom/common-booking/utils/common-booking.utils';
import { profileOrNot } from '@fcom/core/selectors';
import { ProfileTier } from '@fcom/core-api/login';
import { getTripTypeFromBounds, resolvePurchaseFlow } from '@fcom/common/utils/gtm.utils';
import { globalBookingPaxAmount, globalBookingFlights, globalBookingDiscountCode } from '@fcom/common/store';
import { PaxAmount } from '@fcom/dapi';
import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { finShare } from '@fcom/rx';

import {
  cartData,
  inboundOffersSelectionGtmData,
  inboundSelectionGtmData,
  outboundOffersSelectionGtmData,
  outboundSelectionGtmData,
} from '../store/selectors';

@Injectable()
export class BookingGtmService implements OnDestroy {
  private sendFlightSelectionDataCloud$: Subject<void> = new Subject<void>();
  private subscription = new Subscription();

  constructor(
    private store$: Store<BookingAndAppState>,
    private gtmService: GtmService,
    private dataCloudService: DataCloudService
  ) {
    this.initSendFlowData();
    this.initSendCompletePurchase();
    this.initSendCheckoutFlights();
    this.initSendFlightSelection();
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  initSendFlowData(): void {
    this.subscription.add(
      this.store$
        .pipe(
          cartData(),
          distinctUntilChanged((a, b) => {
            // Ensure that we only send flowData one time by checking that has flights changed
            return entrySet(a.controlData?.sellAncillariesPerFlight).every(
              (entry) => entry.value === b.controlData?.sellAncillariesPerFlight[entry.key]
            );
          }),
          withLatestFrom(this.store$.pipe(profileOrNot()))
        )
        .subscribe(([data, profile]) => {
          const isAward = isAwardBooking(data.prices.unpaid);
          this.gtmService.flowData(data.controlData, resolvePurchaseFlow(profile, isAward));
        })
    );
  }

  initSendCompletePurchase(): void {
    this.subscription.add(
      this.store$
        .pipe(
          paymentStatus(),
          filter((s) => s === PaymentStatus.VERIFIED),
          switchMap(() =>
            this.store$.pipe(
              orderData(),
              filter((v) => !!v),
              take(1)
            )
          ),
          withLatestFrom(
            this.store$.pipe(globalBookingFlights()),
            this.store$.pipe(profileOrNot()),
            this.store$.pipe(globalBookingDiscountCode())
          )
        )
        .subscribe(([orderDataValue, flights, profile, discountCode]) => {
          const tripType = getTripTypeFromBounds(orderDataValue.bounds);
          // Check for the existance of inbound data obj to figure out the trip type
          const totalPrice = orderDataValue.prices.included?.total?.total;
          const total = totalPrice?.totalAmount;
          const totalPoints = totalPrice?.totalPoints?.amount;
          const isAward = orderDataValue.serviceFlow === ServiceFlow.AWARD;
          this.gtmService.completePurchase(
            orderDataValue,
            tripType,
            total,
            flights[0] || { origin: undefined, destination: undefined },
            totalPoints,
            resolvePurchaseFlow(profile, isAward),
            orderDataValue.serviceFlow === ServiceFlow.BOOKING ? discountCode : undefined
          );
          this.dataCloudService.completePurchase();
        })
    );
  }

  initSendCheckoutFlights(): void {
    this.subscription.add(
      this.store$
        .pipe(
          cartData(),
          startWith(undefined as FinnairCart),
          bufferCount(2, 1),
          filter(([oldCart, newCart]) => {
            if (typeof oldCart === 'undefined' || typeof newCart === 'undefined') {
              return true;
            }
            const outboundChanged = !equals(oldCart.bounds[0], newCart.bounds[0]);
            const inboundChanged = !equals(oldCart.bounds[1], newCart.bounds[1]);
            return outboundChanged || inboundChanged;
          }),
          map(([, newCart]) => newCart),
          filter((v) => !!v),
          filter((cartDataValue) => !!cartDataValue?.bounds),
          withLatestFrom(this.store$.pipe(profileOrNot()))
        )
        .subscribe(([cartDataValue, profile]) => {
          const isAward = isAwardBooking(cartDataValue.prices.unpaid);
          this.gtmService.checkoutFlights(cartDataValue, resolvePurchaseFlow(profile, isAward));
        })
    );
  }

  initSendFlightSelection(): void {
    // TODO: We should try to combine airBounds (FPP) and offers selectors (FP)
    const flightSelectionData$: Observable<GtmFlightSelectionData> = this.store$.pipe(
      globalBookingPaxAmount(),
      switchMap((paxAmount: PaxAmount) =>
        merge(
          this.store$.pipe(outboundSelectionGtmData(paxAmount)),
          this.store$.pipe(inboundSelectionGtmData(paxAmount)),
          this.store$.pipe(outboundOffersSelectionGtmData(paxAmount)),
          this.store$.pipe(inboundOffersSelectionGtmData(paxAmount))
        )
      ),
      withLatestFrom(this.store$.pipe(profileOrNot())),
      map(([data, profile]) =>
        profile?.tier === ProfileTier.CORPORATE ? { ...data, purchaseFlow: GtmPurchaseFlow.CORPORATE } : data
      ),
      finShare()
    );
    this.subscription.add(
      flightSelectionData$.subscribe((enhancedData) => {
        this.gtmService.flightSelection(GtmEvent.FLIGHT_SELECTION_CLICK, [enhancedData]);
      })
    );
    this.subscription.add(
      this.sendFlightSelectionDataCloud$
        .pipe(withLatestFrom(flightSelectionData$))
        .subscribe(([, enhancedData]) => this.dataCloudService.flightSelectionSelect([enhancedData]))
    );
  }

  sendFlightSelectionDataCloud(): void {
    this.sendFlightSelectionDataCloud$.next();
  }
}
